
import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import DocumentationSidebar from "@/components/documentation/DocumentationSidebar";
import MobileDocumentationTabs from "@/components/documentation/MobileDocumentationTabs";
import GettingStartedSection from "@/components/documentation/GettingStartedSection";
import LeadManagementSection from "@/components/documentation/LeadManagementSection";
import CompanyManagementSection from "@/components/documentation/CompanyManagementSection";
import UserManagementSection from "@/components/documentation/UserManagementSection";
import CampaignPlannerSection from "@/components/documentation/CampaignPlannerSection";
import ChangelogSection from "@/components/documentation/ChangelogSection";

const sectionToTabMap: Record<string, string> = {
  "overview": "getting-started",
  "navigation": "getting-started",
  "authentication": "getting-started",
  "dashboard": "getting-started",
  
  "leads-overview": "leads",
  "adding-leads": "leads",
  "lead-details": "leads",
  "lead-enrichment": "leads",
  "lead-scoring": "leads",
  "lead-activities": "leads",
  
  "companies-overview": "companies",
  "creating-company": "companies",
  "company-details": "companies",
  "company-members": "companies",
  "company-switching": "companies",
  "company-web-search": "companies",
  "company-access-requests": "companies",
  
  "user-roles": "users",
  "profile-settings": "users",
  "subscription-plans": "users",
  "sendgrid-setup": "users",
  
  "campaign-overview": "campaign-planner",
  "creating-campaign": "campaign-planner",
  "managing-assets": "campaign-planner",
  "campaign-dashboard": "campaign-planner",
  
  "recent-changes": "changelog",
};

const Documentation = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("getting-started");
  const [targetSection, setTargetSection] = useState<string | null>(null);
  const isMobile = useIsMobile();
  
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      if (hash && sectionToTabMap[hash]) {
        setTargetSection(hash);
        setActiveTab(sectionToTabMap[hash]);
      }
    };
    
    handleHashChange();
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  
  useEffect(() => {
    if (targetSection && sectionRefs.current[targetSection]) {
      setTimeout(() => {
        sectionRefs.current[targetSection]?.scrollIntoView({ behavior: 'smooth' });
        setTargetSection(null);
      }, 100);
    }
  }, [targetSection, activeTab]);
  
  const registerSectionRef = (id: string, element: HTMLElement | null) => {
    sectionRefs.current[id] = element;
  };

  return (
    <div className="py-8">
      <div className="container max-w-7xl mx-auto px-4">
        <div className="mb-8">
          <div className="text-center max-w-2xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold mb-3">Documentation</h1>
            <p className="text-base md:text-lg text-muted-foreground">
              Everything you need to know about Discovery Hub
            </p>
          </div>
          
          <div className="max-w-lg mx-auto mt-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search documentation..."
                className="pl-10 h-12 bg-white/5 border-border"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          
          {isMobile && (
            <div className="mt-4 flex justify-center">
              <MobileDocumentationTabs
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              />
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] xl:grid-cols-[280px_1fr] gap-6 lg:gap-8">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <Card elevation="flat" className="glass-card">
                <CardContent className="p-4">
                  <DocumentationSidebar 
                    activeTab={activeTab} 
                    setActiveTab={setActiveTab}
                  />
                </CardContent>
              </Card>
            </div>
          </aside>
          
          <div className="space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="hidden md:block mb-6">
                <Card elevation="flat" className="glass-card">
                  <CardContent className="p-2">
                    <TabsList className="bg-transparent w-full justify-start">
                      <TabsTrigger value="getting-started">Getting Started</TabsTrigger>
                      <TabsTrigger value="leads">Leads</TabsTrigger>
                      <TabsTrigger value="companies">Companies</TabsTrigger>
                      <TabsTrigger value="users">Users</TabsTrigger>
                      <TabsTrigger value="campaign-planner">Campaign Planner</TabsTrigger>
                      <TabsTrigger value="changelog">Changelog</TabsTrigger>
                    </TabsList>
                  </CardContent>
                </Card>
              </div>
              
              <TabsContent value="getting-started" className="space-y-6">
                <Card elevation="flat" className="glass-card">
                  <CardContent className="p-8">
                    <GettingStartedSection registerSectionRef={registerSectionRef} />
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="leads" className="space-y-6">
                <Card elevation="flat" className="glass-card">
                  <CardContent className="p-8">
                    <LeadManagementSection registerSectionRef={registerSectionRef} />
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="companies" className="space-y-6">
                <Card elevation="flat" className="glass-card">
                  <CardContent className="p-8">
                    <CompanyManagementSection registerSectionRef={registerSectionRef} />
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="users" className="space-y-6">
                <Card elevation="flat" className="glass-card">
                  <CardContent className="p-8">
                    <UserManagementSection registerSectionRef={registerSectionRef} />
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="campaign-planner" className="space-y-6">
                <Card elevation="flat" className="glass-card">
                  <CardContent className="p-8">
                    <CampaignPlannerSection registerSectionRef={registerSectionRef} />
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="changelog" className="space-y-6">
                <Card elevation="flat" className="glass-card">
                  <CardContent className="p-8">
                    <ChangelogSection registerSectionRef={registerSectionRef} />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;
